import { EndOutcome } from '@/features/gameSession';

import { StrokeAttempt } from './StrokeAttempt';

import type { GameFinishEvent } from '@/features/gameSession';
import type { TraceRenderer } from '../rendering/TraceRenderer';
import type { Point } from './StrokeEvaluator';
import type { Cursor, CursorDragResult } from './Cursor';
import type { PathFollower } from './PathFollower';
import type { StrokePreviewer } from './StrokePreviewer';
import type { Strokes } from './Strokes';
import type { TracingGameSoundController } from './TracingGameSoundController';

type DrawingSoundEffects = Pick<TracingGameSoundController, 'startDrawing' | 'stopDrawing' | 'setDrawingMoving'>;

type TracingGameControllerConfig = {
	strokes: Strokes;
	cursor: Cursor;
	pathFollower: PathFollower;
	goalPosition: Point;
	strokePreviewer: StrokePreviewer;
	traceRenderer: TraceRenderer;
	soundEffects: DrawingSoundEffects;
	inputDeadzone: number;
	onStart: () => void;
	onFailure: (onComplete: () => void) => void;
	onVictory: () => void;
	onFinish: (event: GameFinishEvent) => void;
	playVictory: (score: number, onComplete: () => void) => void;
	resetVictory: () => void;
};

type TracingGameStatus = 'animating' | 'playing' | 'failure' | 'victory' | 'jumping-to-goal' | 'disposed';

export class TracingGameController {
	private status: TracingGameStatus = 'animating';
	private attempt!: StrokeAttempt;
	private runId = 0;
	private hasStarted = false;

	constructor(private readonly config: TracingGameControllerConfig) {}

	start() {
		if (this.status === 'disposed') {
			return;
		}

		const { strokes, cursor, pathFollower } = this.config;
		this.runId++;
		this.hasStarted = false;
		this.config.resetVictory();
		this.config.soundEffects.stopDrawing();
		pathFollower.stop();
		this.config.strokePreviewer.stop();
		this.status = 'animating';
		cursor.setEnabled(false);
		pathFollower.enter(
			strokes.getActiveStartPosition(),
			strokes.getActiveStartDirection(),
			this.forCurrentRun(() => this.startActiveStroke()),
			strokes.getActiveFollowerOffset(),
		);
	}

	startTrace(x: number, y: number) {
		if (this.status === 'playing' && this.attempt.startAt({ x, y })) {
			this.config.cursor.setPosition(x, y);
			this.config.traceRenderer.drawPoint({ x, y });
		}
	}

	endTrace() {
		this.config.soundEffects.setDrawingMoving(false);
	}

	drawTrace(fromX: number, fromY: number, toX: number, toY: number): CursorDragResult {
		const from = { x: fromX, y: fromY };
		if (this.status !== 'playing') {
			return from;
		}

		const result = this.attempt.trace(from, { x: toX, y: toY });
		if (!result) {
			return from;
		}

		this.config.soundEffects.setDrawingMoving(result.position.x !== fromX || result.position.y !== fromY);
		this.config.traceRenderer.drawSegment(from, result.completion?.point ?? result.position);
		if (result.completion) {
			this.config.traceRenderer.drawSegment(result.completion.point, result.position);
		}
		this.config.traceRenderer.setDistance(this.config.strokes.getActiveDistanceTo(result.position));

		if (result.completed) {
			this.completeActiveStroke();
			return { ...result.position, suspendUntilRelease: true };
		}

		if (result.outOfInk) {
			this.failActiveStroke();
			return { ...result.position, suspendUntilRelease: true };
		}

		return result.position;
	}

	retry() {
		if (this.status === 'disposed') {
			return;
		}

		const { traceRenderer, strokes, cursor } = this.config;
		traceRenderer.clear();
		strokes.reset();
		const start = strokes.getActiveStartPosition();
		cursor.setPosition(start.x, start.y);
		this.start();
	}

	dispose() {
		if (this.status === 'disposed') {
			return;
		}

		this.status = 'disposed';
		this.runId++;
		this.config.soundEffects.stopDrawing();
		this.config.pathFollower.stop();
		this.config.strokePreviewer.stop();
		this.config.resetVictory();
	}

	private startActiveStroke() {
		const { strokes, cursor, inputDeadzone, traceRenderer } = this.config;
		traceRenderer.beginStroke();
		const start = strokes.getActiveStartPosition();
		this.attempt = new StrokeAttempt({
			start,
			maxInk: strokes.getActiveMaxInk(),
			inputDeadzone,
			evaluateSegment: strokes.traceSegment.bind(strokes),
		});
		cursor.setPosition(start.x, start.y);
		this.status = 'playing';
		if (!this.hasStarted) {
			this.hasStarted = true;
			this.config.onStart();
		}
		cursor.setEnabled(true);
		this.config.soundEffects.startDrawing();
		this.config.strokePreviewer.play(strokes.getActivePath());
	}

	private failActiveStroke() {
		this.config.strokePreviewer.stop();
		this.config.soundEffects.stopDrawing();
		this.finishGame('failure');
		this.replayStroke(
			this.forCurrentRun(() => {
				let voiceComplete = false;
				let fallComplete = false;
				const retryWhenComplete = this.forCurrentRun(() => {
					if (voiceComplete && fallComplete) {
						this.retry();
					}
				});
				this.config.onFailure(() => {
					voiceComplete = true;
					retryWhenComplete();
				});
				this.config.pathFollower.fall(() => {
					fallComplete = true;
					retryWhenComplete();
				});
			}),
		);
	}

	private completeActiveStroke() {
		const { strokes, cursor, pathFollower } = this.config;
		this.config.strokePreviewer.stop();
		this.config.soundEffects.stopDrawing();
		this.status = 'animating';
		cursor.setEnabled(false);
		this.replayStroke(
			this.forCurrentRun(() => {
				if (this.status !== 'animating') return;
				if (strokes.isFinalStroke()) {
					this.status = 'jumping-to-goal';
					pathFollower.jumpToGoal(
						this.config.goalPosition,
						this.forCurrentRun(() => this.finishGame('victory')),
					);
					return;
				}

				strokes.advanceStroke();
				pathFollower.jumpTo(
					strokes.getActiveStartPosition(),
					strokes.getActiveStartDirection(),
					this.forCurrentRun(() => this.startActiveStroke()),
					strokes.getActiveFollowerOffset(),
				);
			}),
		);
	}

	private replayStroke(onComplete: () => void) {
		const checkpoints = this.attempt.getCrossings();
		const runId = this.runId;
		this.config.pathFollower.follow(this.attempt.getPoints(), checkpoints, onComplete, {
			failed: this.status === 'failure',
			onCheckpoint: (index) => {
				if (runId !== this.runId) return;
				const { checkpointIndex, score } = checkpoints[index];
				this.config.strokes.revealCheckpoint(checkpointIndex, score);
			},
		});
	}

	private finishGame(status: 'failure' | 'victory') {
		if (this.status === 'failure' || this.status === 'victory' || this.status === 'disposed') return;
		this.status = status;

		this.config.cursor.setEnabled(false);
		const { strokes } = this.config;
		const maximumScore = strokes.getMaximumScore();
		const score = maximumScore > 0 ? Math.round((strokes.getScore() / maximumScore) * 100) : 0;
		const event: GameFinishEvent = {
			gameOutcome: status === 'victory' ? EndOutcome.SUCCEEDED : EndOutcome.LOST,
			correctRatio: score / 100,
			score,
		};
		if (status === 'victory') {
			this.config.onVictory();
			this.config.playVictory(
				score,
				this.forCurrentRun(() => this.config.onFinish(event)),
			);
		} else {
			this.config.onFinish(event);
		}
	}

	private forCurrentRun(callback: () => void) {
		const runId = this.runId;
		return () => {
			if (runId === this.runId) {
				callback();
			}
		};
	}
}
